import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Card, 
    CardContent, 
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    FormControlLabel,
    Checkbox,
    Grid,
    useTheme,
    useMediaQuery,
    Tooltip
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningIcon from '@mui/icons-material/Warning';
import { formatCurrency, formatNumber, processDataForAllCampaigns, parseDate, calculateRoas } from './utils';

/**
 * Format currency without decimals specifically for cost and revenue
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency without decimals
 */
const formatCurrencyNoDecimals = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return `$${formatNumber(amount, 0)}`;
};

/**
 * Helper function to calculate day-over-day changes
 * @param {Array} data - Sorted data array
 * @returns {Array} Data with day-over-day changes
 */
const calculateDailyChanges = (data) => {
    if (!data || data.length <= 1) return data;
    
    return data.map((item, index) => {
        if (index === 0) {
            // First day has no previous day to compare with
            return {
                ...item,
                searchesChange: 0,
                cpcChange: 0,
                payoutChange: 0,
                cvrChange: 0,
                roasChange: 0,
                costChange: 0,
                revenueChange: 0,
                total_revenueChange: 0,
                profitChange: 0,
                coverageChange: 0,
                avg_tqChange: 0
            };
        }
        
        const prevItem = data[index - 1];
        
        // Calculate percentage changes
        const searchesChange = prevItem.taboola_clicks !== 0 ? ((item.taboola_clicks - prevItem.taboola_clicks) / prevItem.taboola_clicks) * 100 : 0;
        const cpcChange = prevItem.cpc !== 0 ? ((item.cpc - prevItem.cpc) / prevItem.cpc) * 100 : 0;
        const payoutChange = prevItem.payout !== 0 ? ((item.payout - prevItem.payout) / prevItem.payout) * 100 : 0;
        const cvrChange = prevItem.cvr !== 0 ? ((item.cvr - prevItem.cvr) / prevItem.cvr) * 100 : 0;
        const roasChange = prevItem.roas !== 0 ? ((item.roas - prevItem.roas) / prevItem.roas) * 100 : 0;
        const costChange = prevItem.cost !== 0 ? ((item.cost - prevItem.cost) / prevItem.cost) * 100 : 0;
        const revenueChange = prevItem.revenue !== 0 ? ((item.revenue - prevItem.revenue) / prevItem.revenue) * 100 : 0;
        const total_revenueChange = prevItem.total_revenue !== 0 ? ((item.total_revenue - prevItem.total_revenue) / prevItem.total_revenue) * 100 : 0;
        const profitChange = prevItem.profit !== 0 ? ((item.profit - prevItem.profit) / prevItem.profit) * 100 : 0;
        const coverageChange = prevItem.coverage !== 0 ? ((item.coverage - prevItem.coverage) / prevItem.coverage) * 100 : 0;
        const avg_tqChange = prevItem.avg_tq !== 0 ? Math.round(((item.avg_tq - prevItem.avg_tq) / prevItem.avg_tq) * 100) : 0;
        
        return {
            ...item,
            searchesChange,
            cpcChange,
            payoutChange,
            cvrChange,
            roasChange,
            costChange,
            revenueChange,
            total_revenueChange,
            profitChange,
            coverageChange,
            avg_tqChange
        };
    });
};

/**
 * Format date for display
 * @param {string} dateStr - Date string
 * @returns {string} Formatted date
 */
const formatDateForDisplay = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return dateStr;
    
    // More compact date format: MM/DD
    return `${date.getMonth() + 1}/${date.getDate()}`;
};

/**
 * Component to display change percentage with arrow
 */
const ChangeIndicator = ({ value, isPositiveGood = true }) => {
    if (value === 0) return <span>—</span>;
    
    const isPositive = value > 0;
    const isGood = (isPositive && isPositiveGood) || (!isPositive && !isPositiveGood);
    
    return (
        <Box sx={{ 
            display: 'inline-flex', 
            alignItems: 'center',
            justifyContent: 'flex-start',
            color: isGood ? '#4caf50' : '#f44336',
            fontSize: '0.85rem',
        }}>
            {isPositive ? 
                <ArrowUpwardIcon sx={{ fontSize: '0.85rem' }} /> : 
                <ArrowDownwardIcon sx={{ fontSize: '0.85rem' }} />
            }
            <span>{Math.abs(Math.round(value))}%</span>
        </Box>
    );
};

// Define a consistent table height to prevent layout shifts
const TABLE_HEIGHT = 250;

/**
 * Campaign Trends Table Component
 * @param {Object} props - Component props
 * @param {Array} props.data - Table data (always last 7 days regardless of date filter)
 * @param {string} props.selectedCampaign - Selected campaign ID
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.isAdmin - Whether the current user is an admin
 */
const CampaignTrendsTable = ({ data, selectedCampaign, loading, isAdmin = true }) => {
    // Get theme and media query for responsive design
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    // Log when component receives new data or selected campaign changes
    useEffect(() => {
        // Safely access data length 
        const dataLength = Array.isArray(data) ? data.length : 0;
        const hasData = dataLength > 0;
        
        console.debug('CampaignTrendsTable received data:', {
            dataLength,
            selectedCampaign,
            hasData,
            isAdmin
        });
        
        // Only log TQ values for specific campaigns if we have data
        if (hasData && selectedCampaign && (selectedCampaign === '44286905' || selectedCampaign === '43644095')) {
            const relevantData = data.filter(item => item.sub_id === selectedCampaign);
            console.debug('TQ raw values for campaign:', 
                relevantData.map(item => ({
                    date: item.date,
                    tq_sum: item.tq_sum, 
                    parsed_tq_sum: parseFloat(item.tq_sum) || 0,
                    tq_count: item.tq_count,
                    parsed_tq_count: parseFloat(item.tq_count) || 0
                }))
            );
        }
    }, [data, selectedCampaign, isAdmin]);
    
    // State to track which metrics are visible
    const [visibleMetrics, setVisibleMetrics] = useState({
        searches: true,
        cpc: true,
        payout: true,
        cvr: !isMobile, // Hide CVR on mobile by default
        roas: true,
        cost: !isMobile, // Hide cost on mobile by default
        revenue: true, // Add this line - visible for both admins and users by default
        total_revenue: isAdmin && !isTablet, // Only visible for admins and non-tablet by default
        profit: isAdmin && !isTablet, // Only visible for admins and non-tablet by default
        coverage: !isMobile, // Hide coverage on mobile by default
        avg_tq: !isMobile // Hide avg_tq on mobile by default
    });

    // Update visible metrics when screen size changes
    useEffect(() => {
        // Only update if component is still mounted 
        setVisibleMetrics(prev => ({
            ...prev,
            searches: prev.searches,
            cvr: !isMobile && prev.cvr,
            cost: !isMobile && prev.cost,
            revenue: prev.revenue,
            total_revenue: isAdmin && !isTablet && prev.total_revenue,
            profit: isAdmin && !isTablet && prev.profit,
            coverage: !isMobile && prev.coverage,
            avg_tq: !isMobile && prev.avg_tq
        }));
    }, [isMobile, isTablet, isAdmin]);

    // Early return for empty data - moved to before any data processing
    if (!loading && (!Array.isArray(data) || data.length === 0)) {
        console.debug('CampaignTrendsTable: Early return due to empty data');
        return (
            <Card sx={{ bgcolor: '#1e2130', color: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'white' }}>
                            {selectedCampaign ? `Campaign Performance: ${selectedCampaign}` : 'Overall Campaign Performance'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            *Last 7 days
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: TABLE_HEIGHT }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            No data available for the selected period
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Toggle metric visibility
    const handleMetricToggle = (metric) => {
        setVisibleMetrics(prev => ({
            ...prev,
            [metric]: !prev[metric]
        }));
    };

    // Filter data to selected campaign
    const filteredData = data.filter(item => {
        // Check if the item has a date field
        if (!item.date) {
            return false;
        }

        try {
            // If no campaign is selected, show all campaigns
            // Otherwise, filter by the selected campaign
            const campaignMatch = !selectedCampaign || (
                (item.campaign && item.campaign === selectedCampaign) || 
                (item.sub_id && item.sub_id === selectedCampaign)
            );
            
            // Return true if the campaign matches (or none selected)
            return campaignMatch;
        } catch (error) {
            console.error('Error filtering item:', item, error);
            return false;
        }
    });

    // Preprocess data to ensure tq_sum and tq_count are properly parsed
    const preprocessedData = filteredData.map(item => {
        // Ensure tq_sum and tq_count are parsed as numbers
        return {
            ...item,
            tq_sum: parseFloat(item.tq_sum) || 0,
            tq_count: parseFloat(item.tq_count) || 0
        };
    });

    // Sort filtered data by date
    const sortedFilteredData = [...preprocessedData].sort((a, b) => {
        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);
        if (dateA && dateB) {
            return dateA - dateB;
        }
        return 0;
    });

    // Process single campaign data to ensure all metrics are calculated, grouping by date first
    const processedSingleCampaignData = selectedCampaign ? 
        // First group the data by date, similar to processDataForAllCampaigns
        (() => {
            // Group data by date
            const groupedByDate = sortedFilteredData.reduce((acc, item) => {
                const date = item.date;
                if (!acc[date]) {
                    acc[date] = {
                        date,
                        sub_id: item.sub_id, // Keep campaign ID
                        // Raw values for calculations
                        cost: 0,
                        clicks: 0,
                        conversions: 0,
                        taboola_clicks: 0,
                        revenue: 0,
                        total_revenue: 0,
                        searches: 0,
                        hits: 0,
                        tq_sum: 0,
                        tq_count: 0
                    };
                }
                
                // Sum up raw values for later calculations
                acc[date].cost += parseFloat(item.cost) || 0;
                acc[date].clicks += parseFloat(item.clicks) || 0;
                acc[date].conversions += parseFloat(item.conversions) || 0;
                acc[date].taboola_clicks += parseFloat(item.taboola_clicks) || 0;
                acc[date].revenue += parseFloat(item.revenue) || 0;
                acc[date].total_revenue += parseFloat(item.total_revenue) || 0;
                
                // Sum up searches and hits for coverage calculation
                acc[date].searches += parseFloat(item.searches) || 0;
                acc[date].hits += parseFloat(item.hits) || 0;
                
                // Sum up tq_sum and tq_count for avg_tq calculation
                acc[date].tq_sum += parseFloat(item.tq_sum) || 0;
                acc[date].tq_count += parseFloat(item.tq_count) || 0;
                
                return acc;
            }, {});
            
            // Process each grouped date
            return Object.values(groupedByDate).map(item => {
                // Special debug for campaign 44123876
                if (item.sub_id === '44123876' || selectedCampaign === '44123876') {
                    console.debug('Processing grouped data for campaign 44123876 in CampaignTrendsTable:', {
                        date: item.date,
                        revenue: item.revenue,
                        cost: item.cost,
                        tq_sum: item.tq_sum,
                        tq_count: item.tq_count
                    });
                }
                
                // Calculate CPC as cost/taboola_clicks
                const cpc = item.taboola_clicks > 0 ? item.cost / item.taboola_clicks : 0;
                
                // Calculate coverage as searches/hits
                const coverage = item.hits > 0 ? (item.searches / item.hits) * 100 : 0;
                
                // Calculate avg_tq simply as tq_sum / tq_count
                let avg_tq = 0;
                let tq_status = 'valid';
                
                if (item.tq_count > 0) {
                    // Parse tq_sum as float - fixed to handle string values like "91.74"
                    const tqSum = parseFloat(item.tq_sum) || 0;
                    avg_tq = Math.round(tqSum / item.tq_count);
                    
                    // Update tq_status if we actually have a valid value
                    tq_status = avg_tq > 0 ? 'valid' : 'zero';
                } else {
                    tq_status = 'missing';
                }
                
                // Log TQ calculation for debugging
                if (item.sub_id === '44123876' || selectedCampaign === '44123876') {
                    console.debug('TQ calculation in CampaignTrendsTable after grouping:', {
                        tq_sum: item.tq_sum,
                        parsed_tq_sum: parseFloat(item.tq_sum) || 0,
                        tq_count: item.tq_count,
                        avg_tq: avg_tq,
                        tq_status: tq_status,
                        date: item.date
                    });
                }
                
                // Calculate derived metrics
                const payout = item.clicks > 0 ? item.total_revenue / item.clicks : 0;
                const profit = item.total_revenue - item.cost;
                
                // Calculate CVR directly
                const cvr = item.taboola_clicks > 0 ? (item.conversions / item.taboola_clicks) * 100 : 0;
                
                // Calculate ROAS directly as revenue/cost
                const roas = item.cost > 0 ? item.revenue / item.cost : 0;
                
                return {
                    ...item,
                    cpc,
                    payout,
                    cvr,
                    roas,
                    profit,
                    coverage,
                    avg_tq,
                    tq_status
                };
            });
        })()
    : sortedFilteredData;

    // If no campaign is selected, use processDataForAllCampaigns
    const tableData = selectedCampaign 
        ? (processedSingleCampaignData || []) 
        : (Array.isArray(sortedFilteredData) ? processDataForAllCampaigns(sortedFilteredData) : []);
    
    // Calculate profit for each row if not already present
    const dataWithProfit = Array.isArray(tableData) ? tableData.map(row => {
        if (!row) return null;
        
        if (row.profit === undefined) {
            // Calculate profit as total_revenue - cost
            const profit = (parseFloat(row.total_revenue) || 0) - (parseFloat(row.cost) || 0);
            return { ...row, profit };
        }
        return row;
    }).filter(Boolean) : []; // Remove any null entries
    
    // Add debug logging for TQ values
    if (selectedCampaign === '44286905' || selectedCampaign === '43644095' && dataWithProfit.length > 0) {
        console.debug('Data with profit before calculating changes:', dataWithProfit.map(row => ({
            date: row.date,
            tq_sum: row.tq_sum,
            tq_count: row.tq_count,
            avg_tq: row.avg_tq,
            tq_status: row.tq_status
        })));
    }
    
    // Calculate day-over-day changes
    const dataWithChanges = calculateDailyChanges(dataWithProfit);

    // If loading, show a loading indicator
    if (loading) {
        return (
            <Card sx={{ bgcolor: '#1e2130', color: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'white' }}>
                            {selectedCampaign ? `Campaign Performance: ${selectedCampaign}` : 'Overall Campaign Performance'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            *Always showing last 7 days
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: TABLE_HEIGHT }}>
                        <CircularProgress sx={{ color: 'white' }} />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // If no data is available, show a message
    if (dataWithChanges.length === 0) {
        return (
            <Card sx={{ bgcolor: '#1e2130', color: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'white' }}>
                            {selectedCampaign ? `Campaign Performance: ${selectedCampaign}` : 'Overall Campaign Performance'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            *Always showing last 7 days
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: TABLE_HEIGHT }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            No data available for the selected period
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ bgcolor: '#1e2130', color: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <CardContent sx={{ padding: '16px', '&:last-child': { paddingBottom: '16px' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'white', fontSize: '1rem' }}>
                        {selectedCampaign ? `Campaign Performance: ${selectedCampaign}` : 'Overall Campaign Performance'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        *Last 7 days
                    </Typography>
                </Box>
                
                {/* Metric toggles */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.searches}
                                    onChange={() => handleMetricToggle('searches')}
                                    sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="Searches"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.cpc}
                                    onChange={() => handleMetricToggle('cpc')}
                                    sx={{ color: '#8884d8', '&.Mui-checked': { color: '#8884d8' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="CPC"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.payout}
                                    onChange={() => handleMetricToggle('payout')}
                                    sx={{ color: '#82ca9d', '&.Mui-checked': { color: '#82ca9d' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="Payout"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.cvr}
                                    onChange={() => handleMetricToggle('cvr')}
                                    sx={{ color: '#ffc658', '&.Mui-checked': { color: '#ffc658' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="CVR"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.roas}
                                    onChange={() => handleMetricToggle('roas')}
                                    sx={{ color: '#ff6b81', '&.Mui-checked': { color: '#ff6b81' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="ROAS"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.cost}
                                    onChange={() => handleMetricToggle('cost')}
                                    sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="Cost"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.coverage}
                                    onChange={() => handleMetricToggle('coverage')}
                                    sx={{ color: '#e67e22', '&.Mui-checked': { color: '#e67e22' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="Coverage"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.avg_tq}
                                    onChange={() => handleMetricToggle('avg_tq')}
                                    sx={{ color: '#3498db', '&.Mui-checked': { color: '#3498db' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="Avg TQ"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    checked={visibleMetrics.revenue}
                                    onChange={() => handleMetricToggle('revenue')}
                                    sx={{ color: '#00bcd4', '&.Mui-checked': { color: '#00bcd4' }, padding: '2px' }}
                                    size="small"
                                />
                            }
                            label="Tab Rev"
                            sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                    </Grid>
                    
                    {/* Admin-only metrics */}
                    {isAdmin && (
                        <>
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={visibleMetrics.total_revenue}
                                            onChange={() => handleMetricToggle('total_revenue')}
                                            sx={{ color: '#2196f3', '&.Mui-checked': { color: '#2196f3' }, padding: '2px' }}
                                            size="small"
                                        />
                                    }
                                    label="Revenue"
                                    sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                                />
                            </Grid>
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={visibleMetrics.profit}
                                            onChange={() => handleMetricToggle('profit')}
                                            sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' }, padding: '2px' }}
                                            size="small"
                                        />
                                    }
                                    label="Profit"
                                    sx={{ color: 'white', margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
                
                <Box sx={{ height: TABLE_HEIGHT, width: '100%' }}>
                    <TableContainer component={Paper} sx={{ 
                        bgcolor: '#1e2130', 
                        height: '100%',
                        maxHeight: TABLE_HEIGHT,
                        width: '100%',
                        overflowY: 'auto',
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        }
                    }}>
                        <Table stickyHeader size="small" sx={{ 
                            tableLayout: 'auto',
                            width: '100%',
                            minWidth: isMobile ? 400 : 'auto',
                            borderCollapse: 'collapse',
                            borderSpacing: 0,
                            '& .MuiTableCell-root': {
                                padding: '2px 4px',
                                fontSize: '0.85rem'
                            }
                        }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        bgcolor: '#2c3040', 
                                        color: 'white', 
                                        fontWeight: 'bold', 
                                        width: '30px',
                                        paddingRight: '2px',
                                        paddingLeft: '4px',
                                        fontSize: '0.85rem'
                                    }}>Date</TableCell>
                                    
                                    {visibleMetrics.searches && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#ff9800', 
                                                fontWeight: 'bold',
                                                width: '50px',
                                                paddingLeft: '0'
                                            }}>Searches</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#ff9800', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {visibleMetrics.cpc && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#8884d8', 
                                                fontWeight: 'bold',
                                                width: '50px',
                                                paddingLeft: '0'
                                            }}>CPC</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#8884d8', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {visibleMetrics.payout && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#82ca9d', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>Payout</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#82ca9d', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {visibleMetrics.cvr && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#ffc658', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>CVR</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#ffc658', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {visibleMetrics.roas && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#ff6b81', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>ROAS</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#ff6b81', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {visibleMetrics.cost && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#9c27b0', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>Cost</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#9c27b0', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {visibleMetrics.coverage && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#e67e22', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>Cover</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#e67e22', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {visibleMetrics.avg_tq && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#3498db', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>TQ</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#3498db', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {visibleMetrics.revenue && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#00bcd4', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>Tab Rev</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#00bcd4', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {/* Admin-only metrics in table header */}
                                    {isAdmin && visibleMetrics.total_revenue && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#2196f3', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>Rev</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#2196f3', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                    
                                    {isAdmin && visibleMetrics.profit && (
                                        <>
                                            <TableCell align="right" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#4caf50', 
                                                fontWeight: 'bold',
                                                width: '50px'
                                            }}>Profit</TableCell>
                                            <TableCell align="left" sx={{ 
                                                bgcolor: '#2c3040', 
                                                color: '#4caf50', 
                                                fontWeight: 'bold',
                                                width: '40px',
                                                paddingLeft: '0'
                                            }}>Δ</TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dataWithChanges.map((row, index) => (
                                    <TableRow 
                                        key={row.date} 
                                        sx={{ 
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            '&:nth-of-type(odd)': { bgcolor: 'rgba(255, 255, 255, 0.03)' },
                                            height: '28px'
                                        }}
                                    >
                                        <TableCell 
                                            component="th" 
                                            scope="row" 
                                            sx={{ 
                                                color: 'white', 
                                                whiteSpace: 'nowrap',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                paddingRight: '2px',
                                                paddingLeft: '4px'
                                            }}
                                        >
                                            {formatDateForDisplay(row.date)}
                                        </TableCell>
                                        
                                        {visibleMetrics.searches && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    {formatNumber(row.taboola_clicks || 0)}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.searchesChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {visibleMetrics.cpc && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    {formatNumber(row.cpc, 2)}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.cpcChange} isPositiveGood={false} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {visibleMetrics.payout && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                                    }}
                                                >
                                                    {formatNumber(row.payout, 2)}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.payoutChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {visibleMetrics.cvr && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                                    }}
                                                >
                                                    {formatNumber(row.cvr, 2)}%
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.cvrChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {visibleMetrics.roas && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                                    }}
                                                >
                                                    {formatNumber(row.roas, 2)}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.roasChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {visibleMetrics.cost && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                                    }}
                                                >
                                                    {formatCurrencyNoDecimals(row.cost)}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.costChange} isPositiveGood={false} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {visibleMetrics.coverage && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                                    }}
                                                >
                                                    {formatNumber(row.coverage, 2)}%
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.coverageChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {visibleMetrics.avg_tq && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255,255,255,0.08)'
                                                    }}
                                                >
                                                    {row.tq_status === 'error' || row.tq_status === 'missing' ? (
                                                        <Box sx={{ textAlign: 'right', width: '100%' }}>
                                                            -
                                                        </Box>
                                                    ) : row.tq_status === 'zero' ? (
                                                        <Box sx={{ textAlign: 'right', width: '100%' }}>
                                                            0
                                                        </Box>
                                                    ) : (
                                                        formatNumber(row.avg_tq, 0)
                                                    )}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.avg_tqChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {visibleMetrics.revenue && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                                    }}
                                                >
                                                    {formatCurrencyNoDecimals(row.revenue)}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.revenueChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {/* Admin-only metrics in table rows */}
                                        {isAdmin && visibleMetrics.total_revenue && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                                    }}
                                                >
                                                    {formatCurrencyNoDecimals(row.total_revenue)}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.total_revenueChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                        
                                        {isAdmin && visibleMetrics.profit && (
                                            <>
                                                <TableCell 
                                                    align="right" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                                                    }}
                                                >
                                                    {formatCurrencyNoDecimals(row.profit)}
                                                </TableCell>
                                                <TableCell 
                                                    align="left" 
                                                    sx={{ 
                                                        color: 'white', 
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                        paddingLeft: '0'
                                                    }}
                                                >
                                                    <ChangeIndicator value={row.profitChange} isPositiveGood={true} />
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CampaignTrendsTable; 